import { compact } from 'lodash';
import { applyMiddleware, createStore } from 'redux';
import thunk from 'redux-thunk';
import { type IPlatformAPI } from '@wix/yoshi-flow-editor';
import {
  FETCH_BLOG_DATA_SUCCESS,
  GLOBALS,
  SET_APP_CONFIG,
} from '@wix/communities-blog-client-common';
import { type PlatformApi } from '@app/external/common/controller/platform-api';
import { type Router } from '@app/external/common/router';
import { type AppStore, type FlowAPI, type ControllerConfig } from '@app/external/common/types';
import { appUouBiMiddleware, appUsersBiMiddleware } from '../../common/bi-events/init-middleware';
import { getInstance } from '../../common/controller/helpers';
import createShowMessage from '../../common/messages/framework/create-show-message';
import { createMessageMiddleware } from '../../common/messages/framework/initialize-store';
import {
  createDuplexerMiddleware,
  socketEventHandlers,
} from '../../common/middleware/duplexer-middleware';
import { postMetadataMiddleware } from '../../common/middleware/post-metadata-middleware';
import { debounceActions, throttleActions } from '../../common/middleware/redux-middlewares';

import {
  createModalClosedMiddleware,
  createModalOpenedMiddleware,
} from '../../common/modals/framework/initialize-store';
import { SET_APP_SETTINGS } from '../../common/store/app-settings/app-settings-actions';
import { SET_BASIC_PARAMS } from '../../common/store/basic-params/basic-params-actions';
import { FETCH_CATEGORIES_SUCCESS } from '../../common/store/categories/fetch-categories';
import { setInstanceValues } from '../../common/store/instance-values/instance-values-actions';
import loadingBarMiddleware from '../../common/store/loading-bar/loading-bar-middleware';
import { FETCH_SITE_PROPERTIES_SUCCESS } from '../../common/store/site-properties/site-properties-types';
import { FETCH_TOPOLOGY_SUCCESS } from '../../common/store/topology/topology-actions';
import { waitForActionMiddleware } from '../../common/store/wait-for-action/wait-for-action-middleware';
import { eventMap } from '../bi-events/uou-events';
import { usersEventMap } from '../bi-events/users-events';
import messageResolvers from '../components/messages/message-resolvers';
import { modalDataLoaderByType } from '../components/modals/modal-data-loader-map-by-type';
import { modalResolverMapByType } from '../components/modals/modal-resolver-map-by-type';
import reducers from '../reducers';
import { type PostPageWidgetThunkArgs } from '../types';

export function createReduxStore({
  appParams,
  platformApi,
  compId,
  platformAPIs,
  getRouter,
  isSSR,
  isEditor,
  isPreview,
  language,
  flowAPI,
}: {
  appParams: ControllerConfig['appParams'];
  platformApi: PlatformApi;
  compId: ControllerConfig['compId'];
  platformAPIs: IPlatformAPI;
  getRouter: () => Router;
  language: string;
  bundleName: string;
  flowAPI: FlowAPI;
  isSSR: boolean;
  isEditor: boolean;
  isPreview: boolean;
}) {
  const isSeo = platformApi.seo.isInSEO();

  const disableDuplexer = (GLOBALS.DISABLE_DUPLEXER_FOR_INSTANCE_IDS || '')
    .split(',')
    .includes(appParams.instanceId);
  const useDuplexer = !isSSR && !isEditor && !isPreview && !isSeo && !disableDuplexer;

  const middleware = compact([
    throttleActions(),
    debounceActions(),
    thunk.withExtraArgument({
      platformApi,
      compId,
      appParams,
      getRouter,
      panoramaClient: flowAPI.panoramaClient,
      httpClient: flowAPI.httpClient,
      errorHandler: flowAPI.errorHandler,
      flowAPI,
    } satisfies PostPageWidgetThunkArgs),
    waitForActionMiddleware([
      SET_APP_SETTINGS,
      SET_APP_CONFIG,
      setInstanceValues.type,
      SET_BASIC_PARAMS,
      FETCH_BLOG_DATA_SUCCESS,
      FETCH_SITE_PROPERTIES_SUCCESS,
      FETCH_TOPOLOGY_SUCCESS,
      FETCH_CATEGORIES_SUCCESS,
    ]),

    createModalClosedMiddleware({ modalResolverMapByType, createShowMessage }),
    createModalOpenedMiddleware({ modalDataLoaderByType }),
    createMessageMiddleware({ resolvers: messageResolvers }),
    postMetadataMiddleware({ isSSR, language }),
    isEditor
      ? appUsersBiMiddleware({
          platformAPIs,
          instanceId: appParams.instanceId,
          eventMap: usersEventMap,
        })
      : appUouBiMiddleware({
          platformAPIs,
          instanceId: appParams.instanceId,
          eventMap,
        }),
    loadingBarMiddleware(),
    useDuplexer &&
      createDuplexerMiddleware({
        getInstance: getInstance(platformApi),
        eventHandlers: socketEventHandlers,
        instanceId: appParams.instanceId,
        captureMessage: (message: string, extra: Record<string, any>) => {
          flowAPI.panoramaClient.errorMonitor().reportError(new Error(message), extra);
        },
      }),
  ]);

  const initialState = { experiments: flowAPI.essentials.experiments.all() };

  return createStore(reducers, initialState, applyMiddleware(...middleware)) as unknown as AppStore;
}
